import type React from 'react';
import { Link } from 'react-router-dom';

interface AuthorAvatarProps {
  username: string;
  avatar: string;
  displayName: string;
  userId?: string; // Add userId prop
  currentUsername?: string;
  currentUserId?: string; // Add current user ID
  size?: 'small' | 'medium' | 'large';
  showName?: boolean;
  className?: string;
  [key: string]: any; // Allow additional props like data attributes
}

const sizeMap = {
  small: { width: 24, height: 24 },
  medium: { width: 32, height: 32 },
  large: { width: 48, height: 48 },
};

export const AuthorAvatar: React.FC<AuthorAvatarProps> = ({
  username,
  avatar,
  displayName,
  userId,
  currentUsername,
  currentUserId,
  size = 'medium',
  showName = true,
  className = '',
  ...rest // Capture any additional props like data attributes
}) => {
  // Only add hovercard data attribute if it's NOT the current user
  const isCurrentUser =
    (userId && currentUserId && userId === currentUserId) ||
    username === currentUsername;

  const hovercardProps =
    !isCurrentUser && userId
      ? {
          'data-username': username,
          'data-user-id': userId,
        }
      : {};
  const dimensions = sizeMap[size];

  const defaultAvatar =
    'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDgiIGhlaWdodD0iNDgiIHZpZXdCb3g9IjAgMCA0OCA0OCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjQiIGN5PSIyNCIgcj0iMjQiIGZpbGw9IiNlMWU4ZWQiLz4KPHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeD0iMTIiIHk9IjEyIj4KPGF0aCBkPSJNMTIgMTJjMi4yMSAwIDQtMS43OSA0LTRzLTEuNzktNC00LTQtNCAxLjc5LTQgNCAxLjc5IDQgNCA0em0wIDJjLTIuNjcgMC04IDEuMzQtOCA0djJoMTZ2LTJjMC0yLjY2LTUuMzMtNC04LTR6IiBmaWxsPSIjNjU3Nzg2Ii8+Cjwvc3ZnPgo8L3N2Zz4=';

  const profilePath = `/user/${username}/profile`;

  return (
    <div
      className={`author-avatar ${className}`}
      style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
    >
      <Link
        data-username={username}
        to={profilePath}
        className="avatar-link hovercard-trigger"
        {...hovercardProps}
        style={{
          display: 'block',
          borderRadius: '50%',
          overflow: 'hidden',
          flexShrink: 0,
        }}
      >
        <img
          src={avatar || defaultAvatar}
          alt={displayName}
          width={dimensions.width}
          height={dimensions.height}
          style={{
            borderRadius: '50%',
            objectFit: 'cover',
            display: 'block',
            width: dimensions.width,
            height: dimensions.height,
            minWidth: dimensions.width,
            minHeight: dimensions.height,
          }}
          onError={(e) => {
            (e.target as HTMLImageElement).src = defaultAvatar;
          }}
        />
      </Link>

      {showName && (
        <Link
          to={profilePath}
          className="username-link hovercard-trigger"
          {...hovercardProps}
          style={{
            textDecoration: 'none',
            color: 'inherit',
            fontWeight: 600,
            fontSize: size === 'small' ? '14px' : '16px',
          }}
        >
          {displayName}
        </Link>
      )}
    </div>
  );
};
